import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { CardData } from "@/components/layout/element-layout/grid-layout/GridLayout";
import { useToast } from "@/hooks/use-toast";
import { useConfirmation } from "@/hooks/use-confirmation";
import { ItemTypeConfig } from "@/pages/unified-items/configs/types";
import { deleteType } from "@/lib/stores/typesStore";

export interface UseVariablesHandlersProps {
  data: any[];
  selectedGame: any;
  config: ItemTypeConfig;
  setIsAddWizardOpen: (open: boolean) => void;
}

export function useVariablesHandlers({
  data,
  selectedGame,
  config,
  setIsAddWizardOpen,
}: UseVariablesHandlersProps) {
  const { toast } = useToast();
  const { confirmation } = useConfirmation();
  const navigate = useNavigate();

  const handleDelete = useCallback(
    async (id: string, gameId: string) => {
      const item = data.find((item: any) => item.id === id);
      const itemName = item?.name || "this variable";

      const confirmDialog = confirmation({
        title: "Delete Variable",
        description: `Are you sure you want to delete "${itemName}"? This action cannot be undone.`,
        confirmText: "Delete",
        cancelText: "Cancel",
        variant: "destructive",
        onConfirm: async () => {
          try {
            await deleteType(id, gameId, toast);
            confirmDialog.dismiss();
          } catch (error) {
            console.error("Error deleting variable:", error);
            confirmDialog.dismiss();
            toast({
              title: "Error",
              description: "Failed to delete variable",
              variant: "destructive",
            });
          }
        },
        onCancel: () => {
          confirmDialog.dismiss();
        },
      });
    },
    [data, confirmation, toast]
  );

  const handleCardClick = useCallback(
    (card: CardData) => {
      navigate(`/variables/${card.id}`, { state: { itemName: card.title } });
    },
    [navigate]
  );

  const handleAdd = useCallback(() => {
    setIsAddWizardOpen(true);
  }, [setIsAddWizardOpen]);

  return {
    handleDelete,
    handleCardClick,
    handleAdd,
  };
}
